import { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/macro";
import cx from "classnames";
import { curveCardinal } from "d3";
import format from "date-fns/format";
import { getAddress } from "ethers";
import { ReactNode, useMemo, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { USD_DECIMALS } from "config/factors";
import { GlvOrMarketInfo, GlvInfoData, MarketsInfoData, useMarketTokensData } from "domain/synthetics/markets";
import { isGlvInfo } from "domain/synthetics/markets/glv";
import { PerformanceSnapshot, PriceSnapshot, formatPerformanceBps } from "domain/synthetics/markets/performance";
import { AprSnapshot, PriceData, useGmGlvPerformance } from "domain/synthetics/markets/useGmGlvPerformance";
import { POOLS_TIME_RANGE_OPTIONS, convertPoolsTimeRangeToPeriod } from "domain/synthetics/markets/usePoolsTimeRange";
import { usePoolsTimeRange } from "domain/synthetics/markets/usePoolsTimeRange";
import { TokensData, getMidPrice } from "domain/synthetics/tokens";
import { useTokensDataRequest } from "domain/synthetics/tokens/useTokensDataRequest";
import { useChainId } from "lib/chains";
import { formatDate } from "lib/dates";
import { useLocalizedMap } from "lib/i18n";
import { bigintToNumber, formatUsdPrice, parseValue } from "lib/numbers";
import { EMPTY_ARRAY } from "lib/objects";
import { usePoolsIsMobilePage } from "pages/Pools/usePoolsIsMobilePage";
import { PoolsDetailsCard } from "pages/PoolsDetails/PoolsDetailsCard";

import { Dropdown } from "components/Dropdown/Dropdown";
import Tabs from "components/Tabs/Tabs";

import { FeeApyLabel } from "../GmList/FeeApyLabel";
import { PerformanceLabel } from "../GmList/PerformanceLabel";

const MARKET_GRAPHS_TYPES = ["price", "performance", "feeApr"] as const;

export type MarketGraphType = (typeof MARKET_GRAPHS_TYPES)[number];

const MARKET_GRAPHS_TABS_LABELS: Record<MarketGraphType, MessageDescriptor> = {
  price: msg`Price`,
  performance: msg`Performance`,
  feeApr: msg`Fee APR`,
};

function normalizeAddress(address?: string): string | undefined {
  if (!address) return undefined;
  try {
    return getAddress(address);
  } catch (_error) {
    return address;
  }
}

function getValueByAddress<T>(map: Record<string, T> | undefined, address?: string): T | undefined {
  if (!map || !address) return undefined;

  const normalized = normalizeAddress(address);
  if (normalized && normalized in map) {
    return map[normalized];
  }

  if (address in map) {
    return map[address];
  }

  const lower = address.toLowerCase();
  const matchingKey = Object.keys(map).find((key) => key.toLowerCase() === lower);
  return matchingKey ? map[matchingKey] : undefined;
}

const getGraphValue = ({
  marketGraphType,
  glvOrMarketInfo,
  glvPerformance,
  gmPerformance,
  aprSnapshotsByAddress,
  marketTokensData,
  tokensData,
  prices,
}: {
  marketGraphType: MarketGraphType;
  glvOrMarketInfo: GlvOrMarketInfo;
  glvPerformance: Record<string, number>;
  gmPerformance: Record<string, number>;
  aprSnapshotsByAddress: AprSnapshot[];
  marketTokensData: TokensData | undefined;
  tokensData: TokensData | undefined;
  prices: PriceData | undefined;
}) => {
  const isGlv = isGlvInfo(glvOrMarketInfo);
  const address = normalizeAddress(isGlv ? glvOrMarketInfo.glvTokenAddress : glvOrMarketInfo.marketTokenAddress);
  const priceAddress = normalizeAddress(isGlv ? glvOrMarketInfo.glvTokenAddress : glvOrMarketInfo.longTokenAddress);
  const latestAprSnapshot = aprSnapshotsByAddress.reduce<AprSnapshot | undefined>(
    (latest, snapshot) => (!latest || snapshot.snapshotTimestamp > latest.snapshotTimestamp ? snapshot : latest),
    undefined
  );
  const latestAprFraction = latestAprSnapshot
    ? bigintToNumber(BigInt(latestAprSnapshot.aprByFee) + BigInt(latestAprSnapshot.aprByBorrowingFee), 28)
    : undefined;

  const priceSnapshots = getValueByAddress(prices, priceAddress);
  const latestSnapshot = priceSnapshots?.reduce<PriceSnapshot | undefined>(
    (latest, snapshot) => (!latest || snapshot.snapshotTimestamp > latest.snapshotTimestamp ? snapshot : latest),
    undefined
  );
  const snapshotPrice = latestSnapshot
    ? getMidPrice({
        minPrice: BigInt(latestSnapshot.minPrice),
        maxPrice: BigInt(latestSnapshot.maxPrice),
      })
    : undefined;
  const fallbackPrice = isGlv
    ? getValueByAddress(marketTokensData, priceAddress)?.prices.minPrice
    : getValueByAddress(tokensData, priceAddress)?.prices.minPrice;
  const tokenPrice = snapshotPrice ?? fallbackPrice;
  const performance = isGlv ? getValueByAddress(glvPerformance, address) : getValueByAddress(gmPerformance, address);
  const valuesMap: Record<MarketGraphType, string | undefined> = {
    performance: performance !== undefined ? formatPerformanceBps(performance) : undefined,
    price: tokenPrice !== undefined ? formatUsdPrice(tokenPrice) : undefined,
    feeApr: latestAprFraction !== undefined ? `${Number((latestAprFraction * 100).toFixed(2))}%` : undefined,
  };

  return valuesMap[marketGraphType];
};

export function MarketGraphs({ glvOrMarketInfo }: { glvOrMarketInfo: GlvOrMarketInfo }) {
  const [marketGraphType, setMarketGraphType] = useState<MarketGraphType>(MARKET_GRAPHS_TYPES[0]);
  const { timeRange, setTimeRange } = usePoolsTimeRange();
  const period = useMemo(() => convertPoolsTimeRangeToPeriod(timeRange), [timeRange]);
  const isGlv = isGlvInfo(glvOrMarketInfo);

  const address = normalizeAddress(isGlv ? glvOrMarketInfo.glvTokenAddress : glvOrMarketInfo.marketTokenAddress);

  const { chainId } = useChainId();
  const { tokensData } = useTokensDataRequest(chainId);

  const { marketTokensData } = useMarketTokensData(chainId, { isDeposit: true, withGlv: true });

  const tokenAddresses = useMemo(() => {
    if (marketGraphType === "performance") {
      const performanceAddress = isGlv ? glvOrMarketInfo.glvTokenAddress : glvOrMarketInfo.marketTokenAddress;
      return [glvOrMarketInfo.longTokenAddress, glvOrMarketInfo.shortTokenAddress, performanceAddress];
    }

    const priceAddress = isGlv ? glvOrMarketInfo.glvTokenAddress : glvOrMarketInfo.longTokenAddress;
    return [priceAddress];
  }, [glvOrMarketInfo, isGlv, marketGraphType]);

  const selectedGmData = useMemo(() => {
    if (isGlv || marketGraphType !== "performance") {
      return undefined;
    }

    return {
      [glvOrMarketInfo.marketTokenAddress]: glvOrMarketInfo,
    } as MarketsInfoData;
  }, [glvOrMarketInfo, isGlv, marketGraphType]);

  const selectedGlvData = useMemo(() => {
    if (!isGlv || marketGraphType !== "performance") {
      return undefined;
    }

    return {
      [glvOrMarketInfo.glvTokenAddress]: glvOrMarketInfo,
    } as GlvInfoData;
  }, [glvOrMarketInfo, isGlv, marketGraphType]);

  const {
    glvPerformance,
    gmPerformance,
    glvPerformanceSnapshots,
    gmPerformanceSnapshots,
    prices,
    aprSnapshots,
    isLoading,
  } = useGmGlvPerformance({
    chainId,
    period,
    gmData: selectedGmData,
    glvData: selectedGlvData,
    tokenAddresses,
    marketAddresses: marketGraphType === "feeApr" && address ? [address] : undefined,
  });

  const aprSnapshotsByAddress = getValueByAddress(aprSnapshots, address) ?? EMPTY_ARRAY;
  // Use long token prices for the graph
  const priceSnapshotsByAddress =
    getValueByAddress(prices, normalizeAddress(glvOrMarketInfo.longTokenAddress)) ?? EMPTY_ARRAY;
  const currentPerformance = isGlv
    ? getValueByAddress(glvPerformance, address)
    : getValueByAddress(gmPerformance, address);
  const performanceSnapshots =
    (isGlv
      ? getValueByAddress(glvPerformanceSnapshots, normalizeAddress(glvOrMarketInfo.glvTokenAddress))
      : getValueByAddress(gmPerformanceSnapshots, normalizeAddress(glvOrMarketInfo.marketTokenAddress))) ?? EMPTY_ARRAY;

  const timeRangeTabs = useMemo(
    () =>
      POOLS_TIME_RANGE_OPTIONS.map((range) => ({
        label: range === "total" ? t`ALL` : range.toUpperCase(),
        value: range,
      })),
    []
  );

  const graphTitleLabelMap = {
    performance: <PerformanceLabel short={false} disableHandleStyle />,
    price: t`Current Price`,
    feeApr: <FeeApyLabel disableHandleStyle />,
  };
  // <PoolsTabs<PoolsTimeRange>
  //   tabs={timeRangeTabs}
  //   selected={timeRange}
  //   setSelected={setTimeRange}
  //   className={isMobile ? "w-full gap-4" : undefined}
  //   itemClassName={isMobile ? "text-center grow" : undefined}
  // />
  const poolsTabs = (
    <Dropdown
      options={timeRangeTabs}
      onSelect={(option) => {
        const index = timeRangeTabs.findIndex((o) => o.value === option.value);
        if (index !== -1) {
          setTimeRange(timeRangeTabs[index].value);
        }
      }}
      selectedOption={timeRangeTabs.find((tab) => tab.value === timeRange)}
      classNameDropdownCurrent="!min-w-85 !bg-gray-darkest !border !border-solid !border-gray-dark !h-25 sm:!h-40"
    />
  );

  const marketGraphsTabsLabelMap = useLocalizedMap(MARKET_GRAPHS_TABS_LABELS);

  const marketGraphsTabs = useMemo(
    () =>
      MARKET_GRAPHS_TYPES.map((type) => ({
        label: marketGraphsTabsLabelMap[type],
        value: type,
      })),
    [marketGraphsTabsLabelMap]
  );

  return (
    <PoolsDetailsCard
      title={
        <div className="flex items-center justify-between gap-8">
          {/* <PoolsTabs<MarketGraphType>
            tabs={marketGraphsTabs}
            selected={marketGraphType}
            setSelected={setMarketGraphType}
          /> */}
          {/* <SwipeTabs
            options={marketGraphsTabs.map((tab) => tab.value)}
            optionLabels={marketGraphsTabsLabelMap}
            option={marketGraphType}
            onChange={setMarketGraphType}
            optionClassname="!px-8 sm:!px-12 !py-3 sm:!py-5 !text-[8px] sm:!text-[12px] !h-18 sm:!h-auto"
            wrapperClassname="!border-white-alpha-8 !p-[1.85px] !pl-1 !pr-2 sm:!p-3"
          /> */}
          <Tabs
            options={marketGraphsTabs}
            selectedValue={marketGraphType}
            onChange={setMarketGraphType}
            regularOptionClassname="!px-8 sm:!px-12 !py-3 sm:!py-5 !text-[8px] sm:!text-[12px] !h-18 sm:!h-auto"
            className="!border-white-alpha-8 !px-2 !py-[1.85px] sm:!p-3 sm:!px-3 sm:!py-3"
            variant="filled"
          />
          <div className="ml-auto">{poolsTabs}</div>
        </div>
      }
    >
      <div className="mt-24 flex">
        <div className="xs:px-24 mx-auto flex max-w-[748px] grow flex-col gap-16 px-12 sm:px-52">
          <GraphValue
            value={getGraphValue({
              marketGraphType,
              glvOrMarketInfo,
              glvPerformance,
              gmPerformance,
              aprSnapshotsByAddress,
              marketTokensData,
              tokensData,
              prices,
            })}
            label={graphTitleLabelMap[marketGraphType]}
            valueClassName={cx({
              "text-primary-alpha-darker": marketGraphType === "performance" && (currentPerformance ?? 0) > 0,
            })}
            isLoading={isLoading}
          />
          <GraphChart
            performanceSnapshots={performanceSnapshots}
            priceSnapshots={priceSnapshotsByAddress}
            marketGraphType={marketGraphType}
            aprSnapshots={aprSnapshotsByAddress}
            isLoading={isLoading}
          />
        </div>
      </div>
    </PoolsDetailsCard>
  );
}

const ACTIVE_DOT_PROPS = {
  r: 8,
  stroke: "var(--color-white)",
  strokeWidth: 8,
};

const DOT_PROPS = {
  r: 0,
};

const GRAPH_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

type GraphData = {
  value: number;
  snapshotTimestamp: Date;
};

function downsampleGraphData(data: GraphData[], maxPoints: number): GraphData[] {
  if (data.length <= maxPoints) {
    return data;
  }

  const stride = Math.ceil(data.length / maxPoints);
  const reduced = data.filter((_item, index) => index % stride === 0);
  const last = data[data.length - 1];
  if (reduced[reduced.length - 1] !== last) {
    reduced.push(last);
  }
  return reduced;
}

const valueFormatter = (marketGraphType: MarketGraphType) => (value: number) => {
  if (value === 0) {
    return "0";
  }

  const valueMap: Record<MarketGraphType, string> = {
    performance: formatPerformanceBps(value),
    price: formatUsdPrice(parseValue(value.toString(), USD_DECIMALS) ?? 0n) || "",
    feeApr: `${Number(value.toFixed(2))}%`,
  };

  return valueMap[marketGraphType];
};

const axisValueFormatter = (marketGraphType: MarketGraphType) => (value: number) => {
  if (value === 0) {
    return "0";
  }

  const valueMap: Record<MarketGraphType, string> = {
    performance: formatPerformanceBps(value),
    price: value.toString(),
    feeApr: `${Number(value.toFixed(2))}%`,
  };

  return valueMap[marketGraphType];
};

const GraphChart = ({
  performanceSnapshots,
  priceSnapshots,
  marketGraphType,
  aprSnapshots,
  isLoading,
}: {
  performanceSnapshots: PerformanceSnapshot[];
  priceSnapshots: PriceSnapshot[];
  marketGraphType: MarketGraphType;
  aprSnapshots: AprSnapshot[];
  isLoading?: boolean;
}) => {
  const performanceData = useMemo(
    () =>
      performanceSnapshots.map((snapshot) => ({
        snapshotTimestamp: new Date(snapshot.snapshotTimestamp * 1000),
        value: snapshot.performance,
      })),
    [performanceSnapshots]
  );

  const priceData = useMemo(
    () =>
      [...priceSnapshots]
        .sort((a, b) => a.snapshotTimestamp - b.snapshotTimestamp)
        .map((snapshot) => ({
          snapshotTimestamp: new Date(snapshot.snapshotTimestamp * 1000),
          value: bigintToNumber(
            getMidPrice({
              minPrice: BigInt(snapshot.minPrice),
              maxPrice: BigInt(snapshot.maxPrice),
            }),
            USD_DECIMALS
          ),
        })),
    [priceSnapshots]
  );

  const apyData = useMemo(
    () =>
      [...aprSnapshots]
        .sort((a, b) => a.snapshotTimestamp - b.snapshotTimestamp)
        .map((snapshot) => ({
          snapshotTimestamp: new Date(snapshot.snapshotTimestamp * 1000),
          value: bigintToNumber(BigInt(snapshot.aprByFee) + BigInt(snapshot.aprByBorrowingFee), 28),
        })),
    [aprSnapshots]
  );

  const formatValue = useMemo(() => valueFormatter(marketGraphType), [marketGraphType]);
  const formatAxisValue = useMemo(() => axisValueFormatter(marketGraphType), [marketGraphType]);

  const isMobile = usePoolsIsMobilePage();
  const maxPoints = isMobile ? 90 : 140;

  const axisTick = useMemo(() => ({ fill: "var(--color-gray-lighter)", fontWeight: 500, fontSize: 12 }), []);

  const CustomYAxisTick = ({ y, payload }) => {
    let formattedValue = payload.value;
    if (marketGraphType === "feeApr" || marketGraphType === "performance") {
      formattedValue = (payload.value * 100).toFixed(2) + "%";
    } else if (marketGraphType === "price") {
      formattedValue = formatUsdPrice(parseValue(payload.value.toString(), USD_DECIMALS) ?? 0n) || "";
    }

    return (
      <g transform={`translate(${0},${y + 2})`}>
        <text x={0} y={0} textAnchor="start" fontSize={12} fill="var(--color-gray-lighter)">
          {formattedValue}
        </text>
      </g>
    );
  };

  const curveFunction = curveCardinal.tension(0.01);
  const [isGraphHovered, setIsGraphHovered] = useState(false);

  const data = useMemo(() => {
    const dataMap = {
      performance: performanceData,
      price: priceData,
      feeApr: apyData,
    };

    return downsampleGraphData(dataMap[marketGraphType], maxPoints);
  }, [apyData, marketGraphType, maxPoints, performanceData, priceData]);

  return (
    <div onMouseEnter={() => setIsGraphHovered(true)} onMouseLeave={() => setIsGraphHovered(false)}>
      {isLoading ? (
        <Skeleton height={isMobile ? 200 : 231} baseColor="#B4BBFF1A" highlightColor="#B4BBFF1A" />
      ) : (
        <ResponsiveContainer height={isMobile ? 200 : 231} width="100%">
          <AreaChart data={data} margin={GRAPH_MARGIN} key={marketGraphType}>
            <defs>
              <linearGradient id={`color-positive`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#0FCF8A" stopOpacity={0.4}></stop>
                <stop offset="75%" stopColor="#0FCF8A" stopOpacity={0.2}></stop>
                <stop offset="100%" stopColor="#0FCF8A" stopOpacity={0}></stop>
              </linearGradient>
            </defs>
            <Area
              key={marketGraphType}
              type={curveFunction}
              dataKey="value"
              stroke="var(--color-primary-alpha-100)"
              fill={isGraphHovered ? "url(#color-positive)" : "transparent"}
              strokeWidth={4}
              dot={DOT_PROPS}
              activeDot={isGraphHovered ? ACTIVE_DOT_PROPS : false}
              isAnimationActive={false}
            />
            <Tooltip cursor={false} content={<GraphTooltip formatValue={formatValue} isVisible={isGraphHovered} />} />
            <XAxis
              dataKey="snapshotTimestamp"
              tickFormatter={(value) => format(value, "dd/MM")}
              tickLine={false}
              axisLine={false}
              tick={axisTick}
              minTickGap={isMobile ? 16 : 24}
            />
            <YAxis
              dataKey="value"
              tickFormatter={formatAxisValue}
              tickLine={false}
              axisLine={false}
              tick={CustomYAxisTick}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const GraphTooltip = ({ active, payload, formatValue, isVisible }: any) => {
  if (isVisible && active && payload && payload.length) {
    const item = payload[0].payload as GraphData;

    return (
      <div className="rounded-4 bg-primary-alpha-darker p-8">
        <span className="text-12 font-medium text-gray-darkest">
          {formatDate(item.snapshotTimestamp.getTime() / 1000)}: {formatValue(item.value)}
        </span>
      </div>
    );
  }

  return null;
};

const GraphValue = ({
  value,
  label,
  valueClassName,
  isLoading,
}: {
  value: string | undefined;
  label: ReactNode;
  valueClassName?: string;
  isLoading?: boolean;
}) => {
  return (
    <div className="flex items-center gap-8">
      <span className={cx("text-10 font-medium sm:text-16", valueClassName)}>
        {isLoading ? <Skeleton width={88} baseColor="#B4BBFF1A" highlightColor="#B4BBFF1A" /> : value ?? "..."}
      </span>
      <span className="text-8 font-medium italic text-[#5C5C5C] sm:text-14">{label}</span>
    </div>
  );
};
